import { initials } from '@concord/domain';
import { Button, Dialog, KeyValue, PatientPage, PatientTop, PhaseButton } from '@concord/ui';
import { type ReactNode, useState } from 'react';
import { type Me, useSignOut } from '../../lib/session';

/**
 * The patient frame. The route to the care navigator is in the header of every patient
 * screen (PAT-02). In-platform messaging is Phase 4; until then it is email and phone.
 */
export function PatientLayout({ me, children }: { me: Me; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const signOut = useSignOut();
  const nav = me.navigator;
  const who = nav?.firstName
    ? `Contact ${nav.firstName}, your care navigator`
    : 'Contact your care navigator';
  return (
    <>
      <PatientTop
        name={`${me.firstName} ${me.lastName}`}
        initials={initials(me)}
        helpLabel={who}
        onHelp={() => setOpen(true)}
        onSignOut={() => signOut('/sign-in')}
      />
      <PatientPage>{children}</PatientPage>
      <ContactNavigator open={open} onOpenChange={setOpen} me={me} />
    </>
  );
}

export function ContactNavigator({
  open,
  onOpenChange,
  me,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  me: Me;
}) {
  const nav = me.navigator;
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={nav?.name ? `${nav.name}, your care navigator` : 'Your care navigator'}
      description="Your care navigator runs your case and is the person to ask about anything."
      footer={
        <>
          <PhaseButton phase="P4">Message in Concord</PhaseButton>
          {nav?.email && (
            <a className="btn" href={`mailto:${nav.email}`}>
              Send an email
            </a>
          )}
        </>
      }
    >
      <KeyValue
        rows={[
          ['Phone', nav?.phone ?? 'Not listed'],
          ['Email', nav?.email ?? 'Not listed'],
          ['Replies', nav?.hours ?? 'Within one business day'],
        ]}
      />
      <p className="sub2" style={{ margin: 0 }}>
        For anything urgent about your health, contact your own doctor.
      </p>
      <Button variant="ghost" onClick={() => onOpenChange(false)} style={{ alignSelf: 'flex-start' }}>
        Close
      </Button>
    </Dialog>
  );
}
